import { forwardRef, useImperativeHandle, useRef } from 'react';

export interface HomeRouteMapHandle {
  getPathElement: () => SVGPathElement | null;
  getStationElements: () => SVGCircleElement[];
}

/** Transit-style 90° route for GSAP stroke reveal (District Network Map motif). */
export const HomeRouteMap = forwardRef<HomeRouteMapHandle>(
  function HomeRouteMap(_props, ref) {
    const pathRef = useRef<SVGPathElement>(null);
    const stationRefs = useRef<(SVGCircleElement | null)[]>([]);

    useImperativeHandle(ref, () => ({
      getPathElement: () => pathRef.current,
      getStationElements: () =>
        stationRefs.current.filter((el): el is SVGCircleElement => el != null),
    }));

    const stations: { cx: number; cy: number; label: string; color: string }[] =
      [
        { cx: 48, cy: 120, label: 'Observation', color: 'var(--se-route-red)' },
        { cx: 148, cy: 56, label: 'Evidence', color: 'var(--se-route-amber)' },
        { cx: 248, cy: 120, label: 'Rubric', color: 'var(--se-route-teal)' },
        { cx: 348, cy: 184, label: 'Conference', color: 'var(--se-midnight-muted)' },
      ];

    return (
      <div
        className="relative overflow-hidden rounded-md border border-[var(--se-rule)] bg-[var(--se-porcelain-panel)] p-4 sm:p-6"
        aria-hidden
      >
        <svg
          viewBox="0 0 396 220"
          className="h-auto w-full max-h-[220px]"
          data-home-route-svg
        >
          <path
            ref={pathRef}
            data-home-route-path
            d="M 48 120 H 120 V 56 H 176 V 120 H 224 V 120 H 296 V 184 H 348"
            fill="none"
            stroke="var(--se-ink-muted)"
            strokeWidth="3"
            strokeLinejoin="miter"
            strokeLinecap="square"
            opacity={0.35}
          />
          <path
            data-home-route-active
            d="M 48 120 H 120 V 56 H 176 V 120 H 224 V 120 H 296 V 184 H 348"
            fill="none"
            stroke="var(--se-route-red)"
            strokeWidth="3"
            strokeLinejoin="miter"
            strokeLinecap="square"
            strokeDasharray="1"
            strokeDashoffset="1"
            opacity={0.95}
          />
          {stations.map((s, i) => (
            <g key={s.label}>
              <circle
                ref={el => {
                  stationRefs.current[i] = el;
                }}
                data-home-route-station
                cx={s.cx}
                cy={s.cy}
                r="8"
                fill="var(--se-porcelain-panel)"
                stroke={s.color}
                strokeWidth="3"
              />
              <text
                x={s.cx}
                y={s.cy + (i === 1 ? -18 : i === 3 ? 28 : 26)}
                textAnchor="middle"
                className="fill-[var(--se-ink-muted)] text-[10px] font-medium"
                style={{ fontFamily: 'inherit' }}
              >
                {s.label}
              </text>
            </g>
          ))}
        </svg>
        <p className="mt-3 text-center text-xs text-[var(--se-ink-muted)]">
          Evaluation route — same geometry used in the dashboard lifecycle band
        </p>
      </div>
    );
  }
);
